import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { C, SERIF, SANS } from "../lib/constants";

// Minimal 4Ever cycle calendar — colours each day by phase across ~90 days
// centred on the current cycle, so users see past & predicted periods.
const PHASE_COLORS = {
  menstrual: "#F7E8E0",
  follicular: "#E4EFE8",
  ovulatory: "#F7EDDA",
  luteal: "#EDE9F4",
  none: "#FFFFFF",
};
const PHASE_TEXT = {
  menstrual: "#8A3E22",
  follicular: "#25523A",
  ovulatory: "#7A5A18",
  luteal: "#4E3F70",
  none: "#7A726E",
};

function phaseForDay(cycleDay, periodLen, cycleLen) {
  if (cycleDay < 1 || cycleDay > cycleLen) return "none";
  const ovulation = cycleLen - 14;
  if (cycleDay <= periodLen) return "menstrual";
  if (cycleDay < ovulation - 1) return "follicular";
  if (cycleDay <= ovulation + 1) return "ovulatory";
  return "luteal";
}

export default function CycleCalendar({ settings, logs = [] }) {
  const today = new Date();
  const [monthCursor, setMonthCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const loggedDates = useMemo(() => new Set((logs || []).map(l => l.date)), [logs]);

  const cycleLen = settings?.cycleLen || 28;
  const periodLen = settings?.periodLen || 5;
  const lastPeriod = settings?.lastPeriod ? new Date(settings.lastPeriod) : null;

  const dayForDate = (d) => {
    if (!lastPeriod) return null;
    const ms = d.setHours(0, 0, 0, 0) - new Date(lastPeriod).setHours(0, 0, 0, 0);
    const diff = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (diff < 0) {
      // walk backwards
      const back = ((-diff) % cycleLen);
      return back === 0 ? cycleLen : cycleLen - back + 1;
    }
    return (diff % cycleLen) + 1;
  };

  const monthLabel = monthCursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const firstDow = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1).getDay();
  const daysInMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const shift = (delta) => {
    const next = new Date(monthCursor);
    next.setMonth(next.getMonth() + delta);
    setMonthCursor(next);
  };

  const legend = [
    ["Menstrual", "menstrual"], ["Follicular", "follicular"],
    ["Ovulatory", "ovulatory"], ["Luteal", "luteal"],
  ];

  return (
    <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => shift(-1)} style={{ background: "none", border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 6, cursor: "pointer", color: C.muted, display: "flex" }}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontFamily: SERIF, fontSize: 18, color: C.dark }}>{monthLabel}</div>
        <button onClick={() => shift(1)} style={{ background: "none", border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 6, cursor: "pointer", color: C.muted, display: "flex" }}>
          <ChevronRight size={16} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 6 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontFamily: SANS, fontSize: 10,
            color: C.mutedLt, letterSpacing: 1 }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateObj = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), day);
          const cycleDay = dayForDate(new Date(dateObj));
          const phase = cycleDay ? phaseForDay(cycleDay, periodLen, cycleLen) : "none";
          const iso = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isToday = dateObj.toDateString() === today.toDateString();
          const isLogged = loggedDates.has(iso);
          return (
            <div key={idx} style={{
              aspectRatio: "1", background: PHASE_COLORS[phase], borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              position: "relative", border: isToday ? `2px solid ${C.dark}` : "1px solid transparent",
              opacity: dateObj > today ? 0.7 : 1,
            }}>
              <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: isToday ? 700 : 500,
                color: PHASE_TEXT[phase] }}>{day}</span>
              {isLogged && (
                <span style={{ position: "absolute", bottom: 3, width: 4, height: 4,
                  borderRadius: "50%", background: C.coral }} />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 12,
        fontFamily: SANS, fontSize: 11, color: C.muted, justifyContent: "center" }}>
        {legend.map(([label, key]) => (
          <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: PHASE_COLORS[key] }} />
            {label}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: C.coral }} /> Logged
        </span>
      </div>
    </div>
  );
}
